import { MARKETING_FAQ_QA } from '../content/marketingFaq';
import {
  DEFAULT_LIVE_CONNECTIONS,
  MAX_LIVE_CONNECTIONS_CAP,
  MAX_MESSAGE_FORMS_PER_ROOM,
  MAX_ROOMS,
  MAX_TEAM_MEMBERS,
  MAX_TIMERS_PER_ROOM,
} from '../lib/planLimits';
import { PRO_BASE_PRICE } from '../lib/pricing';

export const landingSoftwareApplicationSchema: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'preztimer',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description:
    'Веб-платформа для управления таймингом выступлений, спикерами и сообщениями на экране.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'RUB',
  },
};

/** `/features` — расширенное описание функций (сырой HTML + клиентский SEO). */
export const FEATURES_PAGE_SOFTWARE_SCHEMA: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'preztimer',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description:
    'Панель управления, экран таймера для спикера, сообщения и команда в одной комнате. Лицензии Базовый и Профи.',
  featureList: [
    'Панель управления',
    'Экран таймера для спикера',
    'Сообщения в реальном времени',
    'Командная работа в комнате',
    'QR-коды и ссылки для площадки',
    'Обратный и прямой отсчёт',
  ],
  offers: [
    {
      '@type': 'Offer',
      name: 'Базовый',
      price: '0',
      priceCurrency: 'RUB',
    },
    {
      '@type': 'Offer',
      name: 'Профи',
      price: String(PRO_BASE_PRICE.monthly),
      priceCurrency: 'RUB',
    },
  ],
};

export type FaqSchemaItem = { question: string; answer: string };

export function buildFaqPageSchemaFromItems(items: readonly FaqSchemaItem[]): Record<string, unknown> {
  const mainEntity = items.map((e) => ({
    '@type': 'Question',
    name: e.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: e.answer,
    },
  }));
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}

export function buildFaqPageSchema(): Record<string, unknown> {
  return buildFaqPageSchemaFromItems(
    MARKETING_FAQ_QA.map((e) => ({ question: e.question, answer: e.answerPlain })),
  );
}

/** Только Базовый и Профи — Premium в публичную схему не включаем. */
export function buildPricingPageSchema(siteUrl: string): Record<string, unknown> {
  const base = siteUrl.replace(/\/$/, '');
  const pricingUrl = `${base}/pricing`;
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'preztimer',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description:
      'Лицензии Базовый и Профи: комнаты, таймеры на сцене, экран таймера для спикера и команда.',
    url: pricingUrl,
    offers: [
      {
        '@type': 'Offer',
        name: 'Базовый',
        price: '0',
        priceCurrency: 'RUB',
        url: pricingUrl,
        description: `До ${String(MAX_ROOMS.free)} комнат у владельца, до ${String(MAX_TIMERS_PER_ROOM.free)} таймеров в комнате, до ${String(MAX_MESSAGE_FORMS_PER_ROOM.free)} форм сообщений, до ${String(DEFAULT_LIVE_CONNECTIONS.free)} одновременных подключений.`,
      },
      {
        '@type': 'Offer',
        name: 'Профи',
        price: String(PRO_BASE_PRICE.monthly),
        priceCurrency: 'RUB',
        url: pricingUrl,
        description: `Неограниченные комнаты, до ${String(MAX_TIMERS_PER_ROOM.pro)} таймеров в комнате, команда до ${String(MAX_TEAM_MEMBERS.pro)} участников, до ${String(MAX_LIVE_CONNECTIONS_CAP.pro)} подключений в комнате. От ${String(PRO_BASE_PRICE.monthly)} ₽/мес; годовой доступ — ${String(PRO_BASE_PRICE.yearly)} ₽/год.`,
      },
    ],
  };
}

export function buildWebSiteSchema(siteUrl: string): Record<string, unknown> {
  const base = siteUrl.replace(/\/$/, '');
  return {
    '@type': 'WebSite',
    name: 'preztimer',
    url: `${base}/`,
    description:
      'Веб-сервис контроля тайминга выступлений: панель управления, экран таймера для спикера, команда в одной комнате.',
    publisher: {
      '@type': 'Organization',
      name: 'preztimer',
      url: `${base}/`,
    },
  };
}

/** Главная: SoftwareApplication + WebSite в одном JSON-LD. */
export function buildHomePageJsonLd(
  siteUrl: string,
  softwareSchema: Record<string, unknown>,
): Record<string, unknown> {
  const webSite = buildWebSiteSchema(siteUrl);
  return {
    '@context': 'https://schema.org',
    '@graph': [{ ...softwareSchema, '@context': 'https://schema.org' }, webSite],
  };
}

/** Breadcrumb для `/use-cases/conferences` и `/use-cases/presentations`. */
export function buildUseCasePageBreadcrumbSchema(
  siteUrl: string,
  pagePath: string,
  pageName: string,
): Record<string, unknown> {
  const base = siteUrl.replace(/\/$/, '');
  const path = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
  const pageUrl = `${base}${path}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Главная',
        item: `${base}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: pageName,
        item: pageUrl,
      },
    ],
  };
}

/** Базовая Organization-схема для index.html. URL/logo подставляются на этапе сборки. */
export function buildOrganizationSchema(siteUrl: string): Record<string, unknown> {
  const base = siteUrl.replace(/\/$/, '');
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'preztimer',
    url: `${base}/`,
    logo: `${base}/og-image.png`,
  };
}
